import { useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import { ROUTES } from '../../constants/routes';
import { UserIcon } from '../common/TlIcons';
import { useAuth } from '../../context/AuthContext';
import Logo from '../common/Logo';

// The public site's header, rebuilt to the v5 reference's own chrome.
//
// The reference ships this as a custom element (`tl-extras`) with a shadow
// root, a stylesheet and a `layout()` method that reads window.innerWidth on
// every resize to show and hide pieces. None of that survives here: the
// markup is MUI, the styling is `sx`, and every one of those JS-driven
// show/hide rules is a media query instead, at the reference's own exact
// thresholds rather than MUI's default breakpoints — see BP below.
//
// Two deliberate departures from the reference:
//   · Its four-language switcher (English / Hindi / Telugu / Kannada) is
//     left out at the user's request, along with the phrase table that
//     drove it. Nothing else in the bar depended on it.
//   · It has no cart badge, notification bell, city picker, "My Reports"
//     pill or profile dropdown, and no scroll-shrink — so neither does
//     this. The cart now lives inside Tests / Test Detail / Booking, and
//     signing out moved to the Account page, both per the reference.

// The reference's breakpoints are 760 / 1280 / 1560 — none of which line up
// with MUI's sm/md/lg/xl. Using the nearest MUI step would move the layout
// by up to 80px, so the exact pixel values are kept as raw media queries.
const BP = {
  // Below this the avatar folds into the drawer.
  up760: '@media (min-width:760px)',
  // The inline nav appears here; below it, the burger takes over.
  up1280: '@media (min-width:1280px)',
  // The search button keeps its label only once there is room for it.
  up1560: '@media (min-width:1560px)',
};

// The five links the reference puts in the bar itself.
const BAR_LINKS = [
  { to: ROUTES.TESTS, label: 'Tests' },
  { to: ROUTES.PACKAGES, label: 'Packages' },
  { to: ROUTES.ABOUT, label: 'About' },
  { to: ROUTES.PARTNERS, label: 'Partners' },
  { to: ROUTES.CENTRES, label: 'Branches' },
];

// The drawer carries a longer list than the bar — the reference adds Health
// guides and Help & contact, which have no room in the bar at any width.
const DRAWER_LINKS = [
  { to: ROUTES.TESTS, label: 'Tests' },
  { to: ROUTES.PACKAGES, label: 'Packages' },
  { to: ROUTES.BLOG, label: 'Health guides' },
  { to: ROUTES.CENTRES, label: 'Branches' },
  { to: ROUTES.ABOUT, label: 'About' },
  { to: ROUTES.PARTNERS, label: 'Partners' },
  { to: ROUTES.HELP, label: 'Help & contact' },
];

// The search panel's "What are you worried about?" grid.
const CATEGORIES = [
  'Diabetes', 'Heart & cholesterol', 'Thyroid', 'Tiredness & weakness',
  'Anaemia & blood', 'Vitamins', 'Liver', 'Kidney & urine',
  'Fever & infection', "Women's health", 'Cancer screening', 'Genetic tests',
];

const POPULAR = [
  'Complete Blood Count', 'HbA1c', 'Lipid Profile',
  'Thyroid Profile', 'Vitamin D', 'Vitamin B12',
];

const SHORTCUTS = [
  {
    to: ROUTES.BUILD_PACKAGE,
    title: 'Build your own package',
    sub: 'Pick what worries you. A doctor checks it.',
  },
  {
    to: ROUTES.DOCTOR_CONSULT,
    title: 'Talk to a lab doctor',
    sub: 'Free help choosing the right tests',
  },
];

const SearchGlyph = () => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    aria-hidden="true"
    sx={{ width: 18, height: 18, flexShrink: 0 }}
  >
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </Box>
);

// A 44px circle on the bar's own mid-green. The reference styles the
// avatar, the burger and the drawer's close button identically.
const circle = {
  width: 44,
  height: 44,
  flexShrink: 0,
  borderRadius: '50%',
  bgcolor: '#16413A',
  color: '#fff',
  p: 0,
  '&:hover': { bgcolor: '#1D5048' },
};

const sectionLabel = {
  fontFamily: "'Manrope', sans-serif",
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: '.08em',
  textTransform: 'uppercase',
  color: '#9FC2BA',
};

const Navbar = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, isAuthenticated } = useAuth();

  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');

  const closeMenu = () => setMenuOpen(false);
  const closeSearch = () => setSearchOpen(false);

  // Two characters, as the reference's avatar shows. Falls back to a dot
  // rather than an empty circle for an account with no name on it.
  const initials = (user?.name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('') || '·';

  // The reference's Account page is our Dashboard. A visitor who is not
  // signed in has nothing there, so the avatar sends them to sign in.
  const accountRoute = isAuthenticated ? ROUTES.DASHBOARD : ROUTES.LOGIN;
  const onAccount = pathname === ROUTES.DASHBOARD;

  const submitSearch = (e) => {
    e.preventDefault();
    const v = query.trim();
    closeSearch();
    navigate(v ? `${ROUTES.TESTS}?q=${encodeURIComponent(v)}` : ROUTES.TESTS);
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: '#0B2B27',
          color: '#E4F1ED',
          backgroundImage: 'none',
          boxShadow: 'none',
        }}
      >
        <Box
          sx={{
            maxWidth: 1320,
            width: '100%',
            mx: 'auto',
            px: 2.5,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            // The reference's 24px gap, tightened on phones where the logo,
            // the CTA, search and the burger have to share 390px.
            columnGap: { xs: 0.75, sm: 3 },
          }}
        >
          {/* Logo. The wobble is the reference's own `tlwobble` keyframes,
              declared here rather than in a stylesheet so it travels with
              the component. Suppressed under prefers-reduced-motion: it is
              a decorative rotation and nothing depends on it. */}
          <Box
            component={Link}
            to={ROUTES.HOME}
            aria-label="TrustLab Diagnostics — home"
            sx={{
              display: 'flex',
              lineHeight: 0,
              // The mark yields space before anything else does, so the bar
              // cannot be pushed past a narrow screen by the CTA beside it.
              // minWidth keeps it from collapsing to nothing.
              flexShrink: 1,
              minWidth: 56,
              overflow: 'hidden',
              '@keyframes tlwobble': {
                '0%, 100%': { transform: 'rotate(0deg)' },
                '15%': { transform: 'rotate(-6deg)' },
                '30%': { transform: 'rotate(5deg)' },
                '45%': { transform: 'rotate(-4deg)' },
                '60%': { transform: 'rotate(3deg)' },
                '75%': { transform: 'rotate(-1.5deg)' },
              },
              '&:hover img': { animation: 'tlwobble 0.7s ease-in-out' },
              '@media (prefers-reduced-motion: reduce)': {
                '&:hover img': { animation: 'none' },
              },
            }}
          >
            {/* The reference's own `clamp(50px, 5.4vw, 68px)`, but with a
                smaller floor on phones. 5.4vw never reaches 50px below
                926px, so the floor binds and the mark stays 157px wide —
                which, beside the CTA, the search button and the burger,
                put the right-hand cluster 16px past a 390px viewport in
                the reference itself. */}
            <Logo
              sx={{
                height: { xs: 38, sm: 'clamp(50px, 5.4vw, 68px)' },
                maxWidth: '100%',
                objectFit: 'contain',
                objectPosition: 'left center',
              }}
            />
          </Box>

          {/* Inline nav — centred, and only once there is room at 1280. */}
          <Box
            component="nav"
            sx={{
              display: 'none',
              [BP.up1280]: { display: 'flex' },
              flex: 1,
              minWidth: 0,
              justifyContent: 'center',
              columnGap: 'clamp(2px, 1.4vw, 28px)',
              rowGap: '4px',
            }}
          >
            {BAR_LINKS.map((link) => (
              <Button
                key={link.to}
                component={NavLink}
                to={link.to}
                disableRipple
                sx={{
                  color: '#E4F1ED',
                  fontFamily: "'Manrope', sans-serif",
                  fontWeight: 600,
                  fontSize: 15,
                  textTransform: 'none',
                  borderRadius: '999px',
                  px: '9px',
                  py: '10px',
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                  '&:hover': { bgcolor: '#16413A', color: '#FFFFFF' },
                  // Current page: a solid mint pill. The reference widens
                  // the padding from 9px to 14px on the active item too,
                  // so the pill is not cramped against the label.
                  '&.active': {
                    bgcolor: 'var(--tl-teal-l)',
                    color: 'var(--tl-g9)',
                    fontWeight: 700,
                    px: '14px',
                    '&:hover': { bgcolor: 'var(--tl-teal-l)', color: 'var(--tl-g9)' },
                  },
                }}
              >
                {link.label}
              </Button>
            ))}
          </Box>

          {/* Right cluster. `ml: auto` carries it to the edge at the widths
              where the nav above is hidden and no longer flexes. */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.75, sm: 1 },
              flexShrink: 0,
              ml: 'auto',
            }}
          >
            <Button
              component={Link}
              to={ROUTES.BOOKING}
              sx={{
                bgcolor: '#FCEB00',
                color: '#0B2B27',
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 800,
                fontSize: 15,
                textTransform: 'none',
                borderRadius: '999px',
                // 18px as the reference has it, trimmed on phones for the
                // same width budget the logo above is sized against.
                px: { xs: '14px', sm: '18px' },
                py: '12px',
                minWidth: 0,
                whiteSpace: 'nowrap',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#E8D900', boxShadow: 'none' },
              }}
            >
              Book a test
            </Button>

            <Button
              onClick={() => setSearchOpen(true)}
              aria-label="Search tests"
              startIcon={<SearchGlyph />}
              sx={{
                height: 44,
                minWidth: 0,
                flexShrink: 0,
                borderRadius: '999px',
                bgcolor: '#FFFFFF',
                color: '#0B2B27',
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 700,
                fontSize: 14,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                // Collapses to an icon-only circle below 1560, which is
                // where the reference drops the label.
                px: '13px',
                '& .MuiButton-startIcon': { m: 0 },
                [BP.up1560]: {
                  pl: '14px',
                  pr: '16px',
                  '& .MuiButton-startIcon': { ml: 0, mr: 1 },
                },
                '&:hover': { bgcolor: '#E4F1ED' },
              }}
            >
              <Box
                component="span"
                sx={{ display: 'none', [BP.up1560]: { display: 'inline' } }}
              >
                Search tests
              </Box>
            </Button>

            {/* Avatar → Account. Gets the reference's active treatment when
                it is the current page. */}
            <Button
              component={Link}
              to={accountRoute}
              title="My account"
              aria-label="My account"
              sx={{
                ...circle,
                display: 'none',
                [BP.up760]: { display: 'grid' },
                placeItems: 'center',
                minWidth: 44,
                fontFamily: "'Manrope', sans-serif",
                fontSize: 15,
                fontWeight: 800,
                textTransform: 'none',
                ...(onAccount && {
                  bgcolor: '#5FE0C6',
                  color: '#0B2B27',
                  '&:hover': { bgcolor: '#5FE0C6' },
                }),
              }}
            >
              {/* The reference's prototype always shows a signed-in
                  initial. A visitor who is not signed in has none, so they
                  get the person glyph instead. */}
              {isAuthenticated ? initials : <UserIcon size={20} />}
            </Button>

            {/* Burger — the reference shows it below 1280, not just on
                phones, because the inline nav needs that much room. */}
            <IconButton
              onClick={() => setMenuOpen(true)}
              aria-label="Menu"
              sx={{
                ...circle,
                display: 'inline-flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                [BP.up1280]: { display: 'none' },
              }}
            >
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  component="span"
                  sx={{ width: 18, height: 2, bgcolor: '#fff', borderRadius: '2px' }}
                />
              ))}
            </IconButton>
          </Box>
        </Box>
      </AppBar>

      {/* ── Mobile / tablet drawer ─────────────────────────────────────── */}
      <Drawer
        anchor="right"
        open={menuOpen}
        onClose={closeMenu}
        // PaperProps, not slotProps.paper: MUI 5.18's Drawer and Dialog
        // accept the latter without error and then ignore it, which left
        // both overlays unstyled — a white drawer with white-on-white text.
        PaperProps={{
          sx: {
            width: 'min(100%, 440px)',
            bgcolor: '#0B2B27',
            color: '#fff',
            backgroundImage: 'none',
            boxShadow: '-30px 0 60px -20px rgba(0,0,0,.5)',
            display: 'flex',
            flexDirection: 'column',
            gap: 2.75,
            pt: 1.5,
            px: 2.5,
            pb: 4,
            overflowY: 'auto',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box component={Link} to={ROUTES.HOME} onClick={closeMenu} sx={{ display: 'flex', lineHeight: 0 }}>
            <Logo sx={{ height: 52 }} />
          </Box>
          <IconButton
            onClick={closeMenu}
            aria-label="Close"
            sx={{ ...circle, fontSize: 26, lineHeight: 1 }}
          >
            ×
          </IconButton>
        </Box>

        <Box component="nav" sx={{ display: 'flex', flexDirection: 'column' }}>
          {DRAWER_LINKS.map((link) => (
            <Box
              key={link.to}
              component={NavLink}
              to={link.to}
              onClick={closeMenu}
              sx={{
                color: '#fff',
                textDecoration: 'none',
                fontFamily: "'Bricolage Grotesque', sans-serif",
                fontWeight: 700,
                fontSize: 26,
                letterSpacing: '-.01em',
                py: '13px',
                borderBottom: '1px solid #23463F',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1.5,
                '&::after': { content: '"→"', color: '#5FE0C6', fontSize: 20 },
                '&.active': { color: '#5FE0C6' },
              }}
            >
              {link.label}
            </Box>
          ))}
        </Box>

        <Button
          component={Link}
          to={ROUTES.BOOKING}
          onClick={closeMenu}
          sx={{
            bgcolor: '#FCEB00',
            color: '#0B2B27',
            borderRadius: '16px',
            p: '17px',
            fontFamily: "'Manrope', sans-serif",
            fontWeight: 800,
            fontSize: 17,
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': { bgcolor: '#E8D900', boxShadow: 'none' },
          }}
        >
          Book a test
        </Button>

        <Box
          component={Link}
          to={accountRoute}
          onClick={closeMenu}
          sx={{
            color: '#fff',
            textAlign: 'center',
            fontFamily: "'Manrope', sans-serif",
            fontWeight: 700,
            fontSize: 16,
            textDecoration: 'none',
            p: 0.75,
          }}
        >
          Sign in · My reports · Track sample
        </Box>
      </Drawer>

      {/* ── Search panel ───────────────────────────────────────────────── */}
      <Dialog
        open={searchOpen}
        onClose={closeSearch}
        maxWidth={false}
        aria-label="Search tests"
        sx={{
          '& .MuiBackdrop-root': { bgcolor: 'rgba(11,43,39,.6)' },
          // The reference pins the panel near the top rather than
          // centring it, so the category grid has room to grow downwards.
          '& .MuiDialog-container': { alignItems: 'flex-start' },
        }}
        PaperProps={{
          sx: {
            bgcolor: '#F4F8F6',
            color: '#0B2B27',
            borderRadius: '28px',
            width: '100%',
            maxWidth: 760,
            m: 1.5,
            mt: '4vh',
            maxHeight: '90vh',
            p: 2.25,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            overflowY: 'auto',
            boxShadow: '0 30px 60px -20px rgba(0,0,0,.5)',
          },
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Box
            component="form"
            onSubmit={submitSearch}
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              gap: 1,
              bgcolor: '#fff',
              borderRadius: '18px',
              p: 0.75,
              border: '1.5px solid #D5E5E0',
            }}
          >
            <Box
              component="input"
              type="search"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search 2,000+ tests, e.g. thyroid, sugar, vitamin D"
              aria-label="Search tests"
              sx={{
                flex: 1,
                minWidth: 0,
                border: 0,
                outline: 0,
                bgcolor: 'transparent',
                color: '#0B2B27',
                fontFamily: "'Manrope', sans-serif",
                fontSize: 17,
                p: 1.5,
              }}
            />
            <Button
              type="submit"
              sx={{
                bgcolor: '#0B2B27',
                color: '#fff',
                borderRadius: '14px',
                px: '18px',
                minWidth: 0,
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 800,
                fontSize: 15,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#16413A', boxShadow: 'none' },
              }}
            >
              Search
            </Button>
          </Box>
          <IconButton
            onClick={closeSearch}
            aria-label="Close"
            sx={{
              width: 44, height: 44, flexShrink: 0, p: 0,
              borderRadius: '50%', bgcolor: '#fff', color: '#0B2B27', fontSize: 24,
              '&:hover': { bgcolor: '#E4F1ED' },
            }}
          >
            ×
          </IconButton>
        </Box>

        <Box component="span" sx={{ ...sectionLabel, color: '#5A706B' }}>
          What are you worried about?
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(150px, 100%), 1fr))',
            gap: 0.75,
          }}
        >
          {CATEGORIES.map((c) => (
            <Box
              key={c}
              component={Link}
              to={`${ROUTES.TESTS}?c=${encodeURIComponent(c)}`}
              onClick={closeSearch}
              sx={{
                bgcolor: '#fff',
                borderRadius: '14px',
                px: '14px',
                py: '12px',
                textDecoration: 'none',
                color: '#0B2B27',
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 700,
                fontSize: 14,
                border: '1.5px solid #D5E5E0',
                '&:hover': { borderColor: '#23A991' },
              }}
            >
              {c}
            </Box>
          ))}
        </Box>

        <Box component="span" sx={{ ...sectionLabel, color: '#5A706B' }}>Popular</Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {POPULAR.map((p) => (
            <Box
              key={p}
              component={Link}
              to={`${ROUTES.TESTS}?q=${encodeURIComponent(p)}`}
              onClick={closeSearch}
              sx={{
                bgcolor: '#fff',
                borderRadius: '999px',
                px: '14px',
                py: '9px',
                textDecoration: 'none',
                color: '#0B2B27',
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 700,
                fontSize: 13,
                border: '1.5px solid #D5E5E0',
                '&:hover': { borderColor: '#23A991' },
              }}
            >
              {p}
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {SHORTCUTS.map((card) => (
            <Box
              key={card.to}
              component={Link}
              to={card.to}
              onClick={closeSearch}
              sx={{
                flex: '1 1 200px',
                bgcolor: '#0B2B27',
                color: '#fff',
                borderRadius: '16px',
                px: '16px',
                py: '14px',
                textDecoration: 'none',
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 800,
                fontSize: 14,
              }}
            >
              {card.title}
              <Box
                component="span"
                sx={{
                  display: 'block',
                  color: '#9FC2BA',
                  fontWeight: 600,
                  fontSize: 12,
                  mt: '2px',
                }}
              >
                {card.sub}
              </Box>
            </Box>
          ))}
        </Box>
      </Dialog>
    </>
  );
};

export default Navbar;
